import { useCallback, useEffect, useState } from "react";
import { AuthGate } from "./components/AuthGate";
import { StatusBar } from "./components/StatusBar";
import { TabNav } from "./components/TabNav";
import { SettingsPanel } from "./components/SettingsPanel";
import { TsuyakuTab } from "./tabs/TsuyakuTab";
import { OkoshiTab } from "./tabs/OkoshiTab";
import { ZassoTab } from "./tabs/ZassoTab";
import type { TabId } from "./lib/tabs";
import { DEFAULT_SETTINGS, type Settings } from "./lib/api-types";
import {
  readSelectedDevices,
  type SelectedDevices,
} from "./lib/deviceStorage";

export function App() {
  const [tab, setTab] = useState<TabId>("tsuyaku");
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Settings live at the App level so that changes from SettingsPanel
  // immediately reach the channel tabs without needing a page reload.
  // OkoshiTab still kicks off an initial fetch on mount, but once SettingsPanel
  // performs a PUT the new values propagate via this state.
  const [settings, setSettings] = useState<Settings>(DEFAULT_SETTINGS);

  // Phase 3 / spec §2.8: device selection is stored in localStorage
  // (environment-specific), not in the KV-backed Settings. SettingsPanel
  // owns the write path; the channel tabs read from props so a device
  // change takes effect on the next session.
  const [devices, setDevices] = useState<SelectedDevices>(() =>
    readSelectedDevices(),
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/settings");
        if (!res.ok) return;
        const data = (await res.json()) as Settings;
        if (!cancelled) setSettings(data);
      } catch {
        // Silent — OkoshiTab will display its own error if truly broken.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSettingsChanged = useCallback((next: Settings) => {
    setSettings(next);
  }, []);

  const handleDevicesChanged = useCallback((next: SelectedDevices) => {
    setDevices(next);
  }, []);

  return (
    <AuthGate>
      <div className="flex min-h-screen flex-col bg-cream text-ink">
        <StatusBar tab={tab} onOpenSettings={() => setSettingsOpen(true)} />

        <main className="flex-1 overflow-y-auto">
          {tab === "tsuyaku" && (
            <TsuyakuTab settings={settings} devices={devices} />
          )}
          {tab === "okoshi" && (
            <OkoshiTab
              settings={settings}
              onSettingsChanged={handleSettingsChanged}
              devices={devices}
            />
          )}
          {tab === "zasso" && (
            <ZassoTab settings={settings} devices={devices} />
          )}
        </main>

        <TabNav value={tab} onChange={setTab} />

        {settingsOpen && (
          <SettingsPanel
            onClose={() => setSettingsOpen(false)}
            onSettingsChanged={handleSettingsChanged}
            onDevicesChanged={handleDevicesChanged}
          />
        )}
      </div>
    </AuthGate>
  );
}
